/**
 * GuidedTour — the single tour card used by all Atlas-driven pages.
 *
 * Wraps the existing FeatureCoach `variant="tour"` so the look & feel is
 * identical to the Forecast/Releases benchmark. The companion hook
 * `useGuidedTour` adds the pulsing outline on `#anchorId` for each step.
 *
 * Use `GuidedTour` when the page does not need the hook elsewhere.
 * Use `useGuidedTour` + `GuidedTourCard` when the page must read tour state
 * (e.g. to suppress other coaches).
 */
import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { FeatureCoach } from '../../../components/FeatureCoach/FeatureCoach';
import type { AtlasMission } from '../atlas.types';
import { TourProgressDots } from './TourProgressDots';
import { useGuidedTour, type UseGuidedTourResult } from './useGuidedTour';

export interface GuidedTourSharedProps {
  mission: AtlasMission;
  renderStepBody?: (stepId: string) => React.ReactNode | null;
  renderIdle?: (api: UseGuidedTourResult) => React.ReactNode;
  onBeforeAdvance?: (currentStepId: string) => void;
  /**
   * Optional gate that returns `true` to disable the primary button for the
   * given step id (e.g. Forecast's `filters` step until the user actually
   * touches a filter). `undefined` means never disabled.
   */
  isPrimaryDisabled?: (stepId: string) => boolean;
  /** Optional secondary CTA label (e.g. "Open Settings"). Forwarded to FeatureCoach. */
  actionLabel?: string;
  /** Handler paired with `actionLabel`. */
  onAction?: () => void;
  kicker?: string;
  visible?: boolean;
}

export type GuidedTourCardProps = GuidedTourSharedProps & {
  api: UseGuidedTourResult;
};

export const GuidedTourCard: React.FC<GuidedTourCardProps> = ({
  mission,
  api,
  renderStepBody,
  renderIdle,
  onBeforeAdvance,
  isPrimaryDisabled,
  actionLabel,
  onAction,
  kicker,
  visible = true,
}) => {
  const { theme } = useTheme();

  if (!api.hydrated || api.missingContext) return null;
  if (!api.step) {
    return renderIdle ? <>{renderIdle(api)}</> : null;
  }
  if (!visible) return null;

  const { step, stepIndex, totalSteps } = api;
  const isLast = stepIndex >= totalSteps - 1;
  const body = renderStepBody?.(step.id);
  const primaryDisabled = isPrimaryDisabled?.(step.id) ?? false;

  return (
    <FeatureCoach
      featureKey={mission.featureKey}
      theme={theme}
      show
      variant="tour"
      kicker={kicker ?? mission.kicker}
      title={step.title}
      role="region"
      aria-live="polite"
      headerExtra={<TourProgressDots currentIndex={stepIndex} total={totalSteps} />}
      primaryButtonLabel={step.primaryLabel ?? (isLast ? 'Done' : 'Next →')}
      primaryButtonDisabled={primaryDisabled}
      onPrimaryButtonClick={() => {
        if (primaryDisabled) return;
        onBeforeAdvance?.(step.id);
        api.advance();
      }}
      {...(actionLabel && onAction ? { actionLabel, onAction } : {})}
      skipButtonLabel="Skip tour"
      onSkipButtonClick={api.skip}
      {...(stepIndex > 0 ? { backButtonLabel: '← Back', onBackButtonClick: api.retreat } : {})}
    >
      {body ?? step.body}
    </FeatureCoach>
  );
};

export type GuidedTourProps = GuidedTourSharedProps & {
  autoStart?: boolean;
  anchorHighlight?: boolean;
  /** Re-run anchor highlight when these change (e.g. page left loading so `#anchorId` mounts). */
  anchorExtraDeps?: unknown[];
};

export const GuidedTour: React.FC<GuidedTourProps> = ({
  mission,
  autoStart = true,
  anchorHighlight = true,
  anchorExtraDeps,
  ...rest
}) => {
  const api = useGuidedTour({ mission, autoStart, anchorHighlight, anchorExtraDeps });
  return <GuidedTourCard mission={mission} api={api} {...rest} />;
};

export type { UseGuidedTourResult } from './useGuidedTour';
